/**
 * Copyright (c) 2025-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */

import { faChartArea, faChartLine } from '@fortawesome/free-solid-svg-icons'
import { Children, type FC, type ReactNode } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { useLocation, useMatches } from 'react-router-dom'
import Breadcrumb from '../Breadcrumb'
import NavigationBar from '../NavigationBar'
import { type MenuItem } from './Sidebar'

const DEFAULT_ROUTES: MenuItem[] = [
  {
    title: 'Teste de Sidebar',
    focusedIcon: faChartLine,
    routes: [
      { title: 'Lorem', path: '/', focusedIcon: faChartLine, unfocusedIcon: faChartArea }
    ]
  }
]

export type Props = {
  children?: ReactNode
  routes?: MenuItem[]
}

/**
 * DashboardLayout is described here.
 *
 * @example
 * ```jsx
 * <DashboardLayout />
 * ```
 *
 * @since 09/03/2025
 * @link https://react-styleguidist.js.org/docs/documenting.html
 */
const DashboardLayout: FC<Props> = ({ children, routes = DEFAULT_ROUTES }) => {
  const matches = useMatches()
  const location = useLocation()
  const [currentPage] = matches.filter(match => {
    return match.pathname === location.pathname
  })

  if ((currentPage?.handle as any)?.emptyLayout) {
    return (
      <>{children}</>
    )
  }

  return (
    <>
      <NavigationBar routes={routes} />
      <Container fluid={(currentPage?.handle as any)?.layoutFluid ?? false}>
        <Row>
          <Col md={12}>
            <Breadcrumb />
          </Col>
          <Col md={12}>
            {Children.only(children)}
          </Col>
        </Row>
      </Container>
    </>
  )
}

export default DashboardLayout
